
import { Emitter } from "@utils/events";
import { Component } from "vue-property-decorator";
import { NavigationGuardNext, Route } from "vue-router";
import { BaseView } from "@components/base/view";
import { apiService } from "@services/api.service";
import { AxiosError } from "axios";
import { gtmTrackingService } from "@services/gtm-tracking.service";
import { ciTrackingService } from "@services/ci-tracking.service";
import type { MetaInfo } from "vue-meta";
import { alternateService } from "@services/alternate.service";
import { useLoader } from "@composables/loader";

@Component<any>({
  name: "ci-products-show-wrapper",
  components: {
    "ci-view-product-funds": () => import(/* webpackChunkName: "views_products_show_funds" */ "./show/funds/show.vue"),
  },
  metaInfo(): MetaInfo {
    const title = this.metaOptions.metaTitle;
    const description = this.metaOptions.metaDescription;
    const link = this.metaAlternates;
    return {
      title: title,
      link: link,
      meta: [
        { vmid: "description", name: "description", content: description },
        { vmid: "og:title", property: "og:title", content: title },
        { vmid: "og:description", property: "og:description", content: description },
        { vmid: "twitter:title", name: "twitter:title", content: title },
        { vmid: "twitter:description", name: "twitter:description", content: description },
        { vmid: "twitter:creator", name: "twitter:creator", content: "@CAPinside" },
      ],
    };
  },
})
export default class extends BaseView {
  public product: Nullable<Products.DetailResponseFunds> = null;
  public peergroupRanking: Nullable<Ranking.PeerGroupRanking> = null;
  public peergroupSize: Nullable<number> = null;
  private loader = useLoader();

  public get metaOptions(): Record<string, string> {
    return {
      metaTitle: this.$t("ui.views.products.show.meta_tags.title", {
        name: this.product?.name,
        wkn: this.product?.wkn,
        isin: this.product?.isin,
      }) as string,
      metaDescription: this.$t("ui.views.products.show.meta_tags.description", {
        name: this.product?.name,
        wkn: this.product?.wkn,
        isin: this.product?.isin,
      }) as string,
    };
  }

  public get metaAlternates() {
    return this.product ? alternateService.fromAlternateMap(this.product.alternate_urls) : [];
  }

  public manualGTMUpdate(): boolean {
    return true;
  }

  public async beforeRouteEnter(to: Route, from: Route, next: NavigationGuardNext): Promise<void> {
    next((vm: any) => vm.handleCall(to, from));
  }

  public async beforeRouteUpdate(to: Route, from: Route, next: NavigationGuardNext): Promise<void> {
    if (from.params?.id !== to.params?.id) await this.handleCall(to, from);
    next();
  }

  public mounted() {
    Emitter.on("ci::product::refetch", async () => {
      await this.refetchProductData();
    });
  }

  public async refetchProductData(): Promise<void> {
    const { data: product } = await apiService.products.get(this.$route.params.id);
    this.product = product;
  }

  public async handleCall(to: Route, from: Route): Promise<void> {
    this.initialized = false;
    this.loader.start();
    try {
      const { data: product } = await apiService.products.get(to.params.id);
      this.product = product;

      if (this.product!.peergroup !== null) {
        const { data: rankingData } = await apiService.products.peergroupRankings({
          product_slug: this.product!.slug,
          ranking_parameter: "performance_1_year",
        });

        this.peergroupRanking = rankingData.ranking;
        this.peergroupSize = rankingData.meta.peergroup_size;
      }

      await ciTrackingService.trackView("Product", this.product!.id.toString(), null, null, 1);
      this.gtmReinitialize(to, from, product);
      await this.initialize();
    } catch (err) {
      apiService.handleRoutingAPIError(err as AxiosError, (routerError) => this.loader.setError(routerError));
    } finally {
      this.loader.stop();
    }
  }

  protected gtmReinitialize(to: Route, from: Route, product: Products.DetailResponseFunds): void {
    if (from.name) {
      gtmTrackingService.reinitialize(from.fullPath, to.meta?.pageType, {
        ciReferenceTags: product.ci_reference_tags,
      });
    }
  }
}
